import { router } from '@inertiajs/react';
import confetti from 'canvas-confetti';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { LogoGoogle } from '@/components/icons';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { VoteEdition } from '@/types';
import { googleSignInPath } from './google';

interface UpvoteButtonProps {
  edition: VoteEdition;
  projectSlug: string;
  projectName: string;
  voted: boolean;
  count: number;
  isAuthenticated: boolean;
  hasGoogleAccount: boolean;
  isBelowThreshold: boolean;
}

const CONFETTI_COLORS = ['#e0ff00', '#000000', '#ffffff'];

export function UpvoteButton({
  edition,
  projectSlug,
  projectName,
  voted,
  count,
  isAuthenticated,
  hasGoogleAccount,
  isBelowThreshold,
}: UpvoteButtonProps) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const wasVoted = useRef(voted);
  const font = edition.variant === '25' ? 'font-title' : 'font-sans';

  // The vote itself is a redirect back to this page, so the fresh tally arrives as
  // props. Celebrate when it flips from "not voted" to "voted".
  useEffect(() => {
    if (voted && !wasVoted.current) {
      confetti({
        particleCount: 100,
        angle: 60,
        spread: 70,
        origin: { x: 0, y: 0.6 },
        colors: CONFETTI_COLORS,
        gravity: 0.5,
        startVelocity: 45,
        decay: 0.9,
      });
      confetti({
        particleCount: 100,
        angle: 120,
        spread: 70,
        origin: { x: 1, y: 0.6 },
        colors: CONFETTI_COLORS,
        gravity: 0.5,
        startVelocity: 45,
        decay: 0.9,
      });
      toast.success(`Votaste por ${projectName}`);
    }
    wasVoted.current = voted;
  }, [voted, projectName]);

  const handleVote = () => {
    if (!isAuthenticated || !hasGoogleAccount) {
      setShowModal(true);
      return;
    }

    setError(null);
    router.post(
      `${edition.votePath}/${projectSlug}/toggle`,
      {},
      {
        preserveScroll: true,
        onStart: () => setIsPending(true),
        onFinish: () => setIsPending(false),
        onError: (errors) => {
          const message = (key: string): string | null => {
            const value = errors[key];
            return Array.isArray(value) ? (value[0] ?? null) : (value ?? null);
          };

          // Limit and period problems are toasts with no UI text, as in the Next action.
          const limit = message('voteLimit');
          if (limit) {
            toast.error(limit);
            return;
          }
          const period = message('votePeriod');
          if (period) {
            toast.error(period);
            return;
          }

          const auth = message('voteAuth');
          setError(auth || message('vote') || 'Error al votar');
          if (auth) {
            setShowModal(true);
          }
        },
      },
    );
  };

  const handleGoogleLogin = () => {
    window.location.href = googleSignInPath(
      `${edition.votePath}/${projectSlug}`,
    );
  };

  const breathes =
    !voted &&
    (!isAuthenticated ||
      (isAuthenticated && hasGoogleAccount && isBelowThreshold));

  return (
    <>
      <div className="flex flex-col items-center gap-2">
        {/* Mobile Layout - Rectangular Button */}
        <button
          type="button"
          onClick={handleVote}
          disabled={isPending}
          className={`group flex w-full items-center justify-between border-2 bg-background px-6 py-3 transition-all hover:border-primary disabled:opacity-50 sm:hidden ${
            voted ? 'border-primary' : 'border-primary/30'
          } ${breathes ? 'animate-button-breathe-subtle' : ''}`}
        >
          <span className={`font-bold ${font} text-lg text-primary`}>votar</span>
          <div className="flex items-center gap-3">
            <span className="font-bold font-mono text-lg text-primary">
              {count}
            </span>
            <span className="text-2xl text-primary">▲</span>
          </div>
        </button>

        {/* Desktop Layout - Square Button */}
        <button
          type="button"
          onClick={handleVote}
          disabled={isPending}
          className={`group hidden h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 border-2 bg-background transition-all hover:border-primary disabled:opacity-50 sm:flex lg:h-24 lg:w-24 ${
            voted ? 'border-primary' : 'border-primary/30'
          } ${breathes ? 'animate-button-breathe' : ''}`}
        >
          <div className="text-3xl text-primary transition-all lg:text-4xl">
            ▲
          </div>
          <div className="font-bold font-mono text-primary text-xl transition-all lg:text-2xl">
            {count}
          </div>
        </button>

        {error && (
          <p
            className={`max-w-[200px] text-center ${font} text-primary/60 text-xs`}
          >
            {error}
          </p>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <div className="mb-2 flex items-center justify-center">
              <LogoGoogle size={48} />
            </div>
            <DialogTitle className={`text-center ${font}`}>
              Inicio de sesión con Google requerido
            </DialogTitle>
            <DialogDescription className={`text-center ${font}`}>
              Necesitas iniciar sesión con una cuenta de Google para votar por
              proyectos.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-4">
            <Button
              onClick={handleGoogleLogin}
              className={`w-full border-2 border-primary bg-primary ${font} text-background transition-all hover:bg-primary/90`}
            >
              <div className="mr-2">
                <LogoGoogle size={20} />
              </div>
              Iniciar sesión con Google
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              className={`w-full ${font}`}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
